import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import {
  Type,
  screenXsMin,
  screenSmMax,
  spacer4,
  spacer24,
  spacer16,
  spacer20,
  spacer40,
  spacer72,
} from '@spotify-internal/encore-web';
import { ButtonTertiaryAsLink, ImageAspectBox1x1 } from '../../ui';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import type { SearchHit } from '../../Search/types';
import { transformScaleUp } from '../../utils';
import { useT } from '../../../../features/src/i18n';

const ContentItemWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: ${spacer24} ${spacer72};

  @media (max-width: ${screenSmMax}) {
    padding: ${spacer16};
    flex-direction: column;
  }
`;

const TextWrapper = styled.div`
  padding-left: ${spacer20};
  padding-right: ${spacer40};
  width: 60%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${screenSmMax}) {
    padding-left: 0px;
    width: 90%;
  }
`;

const DetailsWrapper = styled.div`
  width: 20%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${screenSmMax}) {
    width: 100%;
  }
`;

const ImageContainer = styled(ImageAspectBox1x1)`
  padding: ${spacer20};
  width: 15%;
  min-width: 300px;
  height: auto;
  display: flex;
  align-items: center;

  @media (max-width: ${screenSmMax}) {
    display: none;
  }
`;

const MobileDetails = styled.div`
  display: none;

  @media (max-width: ${screenSmMax}) {
    padding-top: ${spacer4};
    display: initial;
  }
`;

const DesktopDetails = styled.div`
  @media (max-width: ${screenSmMax}) {
    display: none;
  }
`;

const Title = styled(Type.h1).attrs({
  paddingBottom: '0.75em',
  variant: 'alto',
})`
  padding-bottom: ${spacer16};
`;

const Description = styled(Type.h1).attrs({
  variant: 'cello',
})``;

const Dateline = styled(Type.h4).attrs({
  variant: 'cello',
})``;

const Location = styled(Type.h4).attrs({
  variant: 'cello',
  weight: 'bold',
})`
  padding: 0px;
`;

const CTABtn = styled(ButtonTertiaryAsLink)`
  background-color: ${props => props.theme.cta.background};

  && {
    color: ${props => props.theme.cta.text};
  }

  @media (max-width: ${screenSmMax}) {
    width: 50%;
    justify-content: center;
  }

  &:hover:not(:disabled),
  &:active:not(:disabled),
  &:focus:not(:disabled) {
    color: ${props => props.theme.cta.text};
    ${transformScaleUp};
  }
`;

const CTAContainer = styled.div`
  display: flex;

  @media (max-width: ${screenXsMin}) {
    width: 100%;
    justify-content: center;
    padding-bottom: ${spacer20};
  }
`;

const TRACK_CAT = 'module-ContentItemsList';

export const ContentItem: React.FC<
  PropsWithChildren<Partial<SearchHit>>
> = hit => {
  const {
    date,
    image,
    title,
    description,
    location,
    linkExternal = false,
    children,
  } = hit;
  const t = useT();
  const formattedDate = date && formatDate(date);

  if (children) {
    return (
      <ContentItemWrapper data-testid="content-item-children">
        {children}
      </ContentItemWrapper>
    );
  }

  return (
    <ContentItemWrapper data-testid="content-item">
      <ImageContainer>
        {image && <ContentfulImage asset={image} />}
      </ImageContainer>
      <TextWrapper>
        <MobileDetails>
          {location && <Location>{location}</Location>}
          {formattedDate && <Dateline>{formattedDate}</Dateline>}
        </MobileDetails>
        <Title>{title}</Title>
        <Description>{description}</Description>
      </TextWrapper>
      <DetailsWrapper>
        <DesktopDetails>
          {location && <Location>{location}</Location>}
          {formattedDate && (
            <Dateline data-testid="content-item-date">{formattedDate}</Dateline>
          )}
        </DesktopDetails>
        {hit.url && (
          <CTAContainer>
            <CTABtn
              href={hit.url}
              external={linkExternal}
              track={[TRACK_CAT, `ContentItem Details Link - ${title}`]}
            >
              {t(
                'FRODOR_CONTENT_ITEM_DETAILS',
                'Details',
                'Refers to item or event details',
              )}
            </CTABtn>
          </CTAContainer>
        )}
      </DetailsWrapper>
    </ContentItemWrapper>
  );
};

function formatDate(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    day: 'numeric',
  });
}
